import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { apiGet, getBackendUrl } from '../utils/api';
import FreeResourcesBar from '../components/FreeResourcesBar';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = ["All", "Career Transformation", "Professional Development", "Job Search", "Career Guides", "Mindset"];

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        console.log('Fetching blogs from:', getBackendUrl() + '/api/blog');
        const data = await apiGet('/api/blog');
        console.log('Blogs received:', data);
        if (isMounted) setPosts(data);
      } catch (e) {
        console.error('Blog fetch error:', e);
        setError('Failed to load blog posts');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const featured = posts.find(p => p.featured) || posts[0];
  const rest = posts.filter(p => !featured || p.id !== featured.id);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      <section className="pt-20 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">Career Blog</h1>
          <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">Practical advice and insights for your IT career journey</p>
        </div>
      </section>

      {/* Free Resources CTA Bar */}
      <FreeResourcesBar />

      {/* Categories (static UI only for now) */}
      <section className="py-8 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  index === 0 ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-slate-700 hover:bg-indigo-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading && (
        <div className="py-12 text-center text-slate-600">Loading articles...</div>
      )}
      {error && (
        <div className="py-12 text-center text-red-600">{error}</div>
      )}

      {!loading && !error && featured && (
        <section className="py-12 bg-[#F9FAFB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Featured Article</h2>
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              <div className="lg:grid lg:grid-cols-2 lg:gap-0">
                <div className="relative h-64 lg:h-auto">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-semibold">{featured.category}</span>
                  </div>
                </div>
                <div className="p-6 lg:p-8">
                  <div className="flex items-center text-sm text-slate-500 mb-4">
                    <User className="h-4 w-4 mr-2" />
                    {featured.author}
                    <Calendar className="h-4 w-4 mx-3" />
                    {featured.date}
                    <Clock className="h-4 w-4 mx-3" />
                    {featured.readTime}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">{featured.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{featured.excerpt}</p>
                  <Link to={`/blog/${featured.id}`}>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900">Read Full Story <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {!loading && !error && rest.length > 0 && (
        <section className="pb-16 bg-[#F9FAFB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Latest Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((post) => (
                <article key={post.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="relative h-48">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-semibold">{post.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-xs text-slate-500 mb-3">
                      <User className="h-3 w-3 mr-1" />
                      {post.author}
                      <Calendar className="h-3 w-3 mx-2" />
                      {post.date}
                      <Clock className="h-3 w-3 mx-2" />
                      {post.readTime}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3 line-clamp-2">{post.title}</h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                    <Link to={`/blog/${post.id}`} className="text-indigo-700 hover:text-indigo-800 font-semibold text-sm flex items-center">Read More <ArrowRight className="ml-1 h-3 w-3" /></Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPage;
